import { useCallback, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { WarningMessage } from 'src/core/POS/MessageWithIcon';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import {
  InventoryMetricsSelectionInputModal,
  SalesMetricsSelectionInputModal,
} from 'src/dialogs/ReportBuilderDialog/MetricsSelectionInputModal';
import { ReportBuilderFilterInput } from 'src/dialogs/ReportBuilderDialog/ReportBuilderFilterInput';
import { ReportConfig, useReportConfigs } from 'src/hooks/useReportConfigs';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { LayoutIcon } from 'src/svgs';
import { ContentId } from 'src/utils/constants/contentId';
import { ReportTypes } from 'src/utils/reportsUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  Permission,
  ReportConfigOverrideOfListingQuery,
  ReportConfigOverrideOfSaleQuery,
  ReportGroupBy,
} from 'src/WebApiController';

import * as styles from './ReportsFilterToolbar.css';

export const ReportsFilterToolbar = ({
  reportConfig,
}: {
  reportConfig: ReportConfig;
}) => {
  const methods = useForm<ReportConfig>({
    defaultValues: reportConfig,
  });
  const { handleSubmit } = methods;
  const { showErrorDialog } = useClientLogContext();
  const [isColumnModalOpen, setColumnModalOpen] = useState(false);

  const {
    upsertReportConfig: upsertReportConfigInventory,
    upsertReportOverride: upsertReportOverrideInventory,
  } = useReportConfigs<ReportTypes.Inventory>({
    reportType: ReportTypes.Inventory,
  });

  const {
    upsertReportConfig: upsertReportConfigSale,
    upsertReportOverride: upsertReportOverrideSale,
  } = useReportConfigs<ReportTypes.Sale>({
    reportType: ReportTypes.Sale,
  });

  const hasUpdateReportFeature = useUserHasAnyOfPermissions(
    Permission.Reports_UpdateReports
  );

  const hasUpdateOwnReportFeature =
    useUserHasAnyOfPermissions(Permission.Reports_UpdateReportsOwned) &&
    reportConfig.isOwner;

  const invokeUpsertReport = useCallback(
    async (reportConfigNew: ReportConfig) => {
      await tryInvokeApi(
        async () => {
          if (reportConfigNew!.globalReportTypeId) {
            if (!hasUpdateReportFeature) {
              // Don't update since the user has no config
              return;
            }

            if (reportConfigNew.reportType === ReportTypes.Inventory) {
              return await upsertReportOverrideInventory(
                {
                  // At the moment we don't supprt persisting filter override for global reports (POS-3604)
                  filter: null,
                  metrics: reportConfigNew.metrics,
                } as ReportConfigOverrideOfListingQuery,
                reportConfig!
              );
            } else {
              // report.reportType === ReportTypes.Sale
              return await upsertReportOverrideSale(
                {
                  filter: null,
                  metrics: reportConfigNew.metrics,
                } as ReportConfigOverrideOfSaleQuery,
                reportConfig!
              );
            }
          } else {
            if (!hasUpdateReportFeature && !hasUpdateOwnReportFeature) {
              // Don't update since the user has no config
              return;
            }

            if (reportConfigNew.reportType === ReportTypes.Inventory) {
              return await upsertReportConfigInventory(
                reportConfigNew.reportId,
                reportConfigNew,
                !reportConfigNew.isOwner
              );
            } else {
              // report.reportType === ReportTypes.Sale
              return await upsertReportConfigSale(
                reportConfigNew.reportId,
                reportConfigNew,
                !reportConfigNew.isOwner
              );
            }
          }
        },
        (error) => {
          showErrorDialog('ReportClient.mergeReport', error, {
            trackErrorData: reportConfigNew,
          });
        }
      );
    },
    [
      hasUpdateOwnReportFeature,
      hasUpdateReportFeature,
      reportConfig,
      showErrorDialog,
      upsertReportConfigInventory,
      upsertReportConfigSale,
      upsertReportOverrideInventory,
      upsertReportOverrideSale,
    ]
  );

  const onReportSaveSubmit = useCallback(
    (report: ReportConfig) => {
      invokeUpsertReport(report);
    },
    [invokeUpsertReport]
  );

  const onColumnSettingButtonClickHandler = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (!isColumnModalOpen) setColumnModalOpen(true);
    },
    [isColumnModalOpen, setColumnModalOpen]
  );

  const onMetrisSelectionModalClose = useCallback(() => {
    handleSubmit(onReportSaveSubmit)();
    setColumnModalOpen(false);
  }, [handleSubmit, onReportSaveSubmit]);

  const filterChangeMessageContentId = useMemo(() => {
    if (reportConfig.globalReportTypeId != null) {
      if (
        reportConfig.groupBy === ReportGroupBy.SaleIdTicketIdCommissionUser ||
        reportConfig.groupBy === ReportGroupBy.ArAgingMarketplace
      ) {
        return ContentId.GlobalReportFilterChangeMessage;
      }
      return ContentId.GlobalReportFilterChangeCopyMessage;
    }
    return undefined;
  }, [reportConfig.globalReportTypeId, reportConfig.groupBy]);

  return (
    (hasUpdateReportFeature || hasUpdateOwnReportFeature) && (
      <FormProvider {...methods}>
        <div className={styles.toolbarContainer}>
          <Stack gap="l" justifyContent="spaceBetween" width="full">
            <ReportBuilderFilterInput
              reportType={reportConfig.reportType}
              showAllButton={false}
              filterAppliedMessage={
                filterChangeMessageContentId && (
                  <WarningMessage
                    message={<Content id={filterChangeMessageContentId} />}
                  />
                )
              }
              embeddedDisplayOnly
            />

            {reportConfig.groupBy !== ReportGroupBy.ArAgingMarketplace ? (
              <Button
                variant="textPlain"
                onClick={onColumnSettingButtonClickHandler}
              >
                <LayoutIcon size={vars.iconSize.m} />
                <Content id={ContentId.Columns} />
              </Button>
            ) : (
              <span />
            )}
          </Stack>
        </div>
        {isColumnModalOpen && (
          <>
            {reportConfig.reportType === ReportTypes.Inventory ? (
              <InventoryMetricsSelectionInputModal
                onClose={onMetrisSelectionModalClose}
                groupBy={reportConfig.groupBy}
              />
            ) : (
              <SalesMetricsSelectionInputModal
                onClose={onMetrisSelectionModalClose}
                groupBy={reportConfig.groupBy}
              />
            )}
          </>
        )}
      </FormProvider>
    )
  );
};
